import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileCheck2, AlertTriangle, ArrowRight, Building2, RotateCcw, Loader2, X, Lock, CheckSquare, Square, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => (v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MESI_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Raggruppa le fatture della preview per anno → mese e ritorna una struttura:
// [{ anno, totale, count, mesi: [{ mese, label, totale, count, items: [{fattura, originalIndex}] }] }]
function groupFattureByYearMonth(fatture) {
  const byYear = new Map();
  fatture.forEach((f, i) => {
    const data = String(f.data || '');
    const year = data.slice(0, 4).match(/^\d{4}$/) ? parseInt(data.slice(0, 4), 10) : 0;
    const month = data.slice(5, 7).match(/^\d{2}$/) ? parseInt(data.slice(5, 7), 10) : 0;
    if (!byYear.has(year)) byYear.set(year, new Map());
    const yMap = byYear.get(year);
    if (!yMap.has(month)) yMap.set(month, []);
    yMap.get(month).push({ fattura: f, originalIndex: i });
  });
  const groups = [];
  Array.from(byYear.keys()).sort((a, b) => b - a).forEach(anno => {
    const yMap = byYear.get(anno);
    const mesi = [];
    let totAnno = 0;
    let countAnno = 0;
    Array.from(yMap.keys()).sort((a, b) => b - a).forEach(m => {
      const items = yMap.get(m);
      const totMese = items.reduce((s, x) => s + (x.fattura.importo || 0), 0);
      mesi.push({
        mese: m,
        label: m > 0 ? MESI_IT[m - 1] : 'Senza data',
        count: items.length,
        totale: totMese,
        items,
      });
      totAnno += totMese;
      countAnno += items.length;
    });
    groups.push({ anno, totale: totAnno, count: countAnno, mesi });
  });
  return groups;
}

// Riga di fattura estratta a componente memoizzato: con centinaia di righe nella
// preview, senza React.memo ogni singolo click su una checkbox re-renderizzava
// l'intera tabella (tutte le righe + i Checkbox Radix montati), causando pesante
// churn del DOM su import di grosse dimensioni. Va definito a livello di modulo
// (non dentro ImportXmlBulkDialog) altrimenti sarebbe un nuovo tipo di componente
// a ogni render del genitore e memo non avrebbe alcun effetto.
// `onRenderProbe`, se passato, è invocato ad ogni esecuzione del render — usato
// solo dai test per verificare che le righe non toccate non si ri-rendano; non ha
// alcun effetto/costo quando non è passato (uso normale).
export const FatturaRow = memo(function FatturaRow({
  index,
  fattura,
  salta,
  duplicato,
  isSelected,
  onToggleSelect,
  onToggleSalta,
  onRenderProbe,
}) {
  onRenderProbe?.(index);
  return (
    <TableRow
      className={duplicato ? 'bg-red-50 opacity-60' : (salta ? 'opacity-50' : '')}
      data-testid={`preview-fattura-${index}`}
    >
      <TableCell className="w-8">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(index)}
          disabled={duplicato}
          aria-label="Seleziona per azione bulk"
          data-testid={`select-row-${index}`}
        />
      </TableCell>
      <TableCell className="w-8">
        {duplicato ? (
          <Lock className="h-4 w-4 text-red-500" aria-label="Duplicato bloccato" />
        ) : (
          <Checkbox
            checked={!salta}
            onCheckedChange={() => onToggleSalta(index)}
            aria-label="Importa fattura"
            data-testid={`preview-check-${index}`}
          />
        )}
      </TableCell>
      <TableCell className="text-xs">{fattura.fornitore_ragione_sociale}</TableCell>
      <TableCell className="text-xs font-mono">{fattura.numero}</TableCell>
      <TableCell className="text-xs">{fattura.data}</TableCell>
      <TableCell className="text-xs">{fattura.scadenza || '—'}</TableCell>
      <TableCell className="text-xs text-right">{fmt(fattura.imponibile)}</TableCell>
      <TableCell className="text-xs text-right">{fmt(fattura.iva)}</TableCell>
      <TableCell className="text-xs text-right font-semibold">{fmt(fattura.importo)}</TableCell>
      <TableCell>
        {duplicato ? (
          <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-300">🔒 Duplicato</Badge>
        ) : fattura.tipo_documento === 'nota_credito' ? (
          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-300">Nota Credito</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">OK</Badge>
        )}
      </TableCell>
    </TableRow>
  );
});
FatturaRow.displayName = 'FatturaRow';

export default function ImportXmlBulkDialog({ open, onClose, onImported }) {
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=riepilogo
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // {fatture, nuovi_fornitori, fornitori_matchati, errori}
  const [nuoviScelte, setNuoviScelte] = useState({}); // ref -> {azione, fornitore_id_esistente?, override fields?}
  const [fattureScelte, setFattureScelte] = useState({}); // index -> {salta, ...overrides}
  const [fornitoriList, setFornitoriList] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [risultato, setRisultato] = useState(null);
  // iter82-fix: gestione gruppi anno-mese collassabili + selezione bulk
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // "anno" or "anno-mese"
  const [selectedIndexes, setSelectedIndexes] = useState(new Set());
  const inputRef = useRef();
  // Tiene `preview` leggibile da callback stabili (identity-invariata tramite
  // useCallback) senza doverlo inserire tra le dipendenze — altrimenti la
  // callback cambierebbe identità ad ogni update di preview, vanificando il
  // React.memo di FatturaRow.
  const previewRef = useRef(null);
  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  useEffect(() => {
    if (open) {
      // Reset when opened
      setStep(1);
      setFiles([]);
      setPreview(null);
      setNuoviScelte({});
      setFattureScelte({});
      setRisultato(null);
      axios.get(`${API}/fornitori`, { withCredentials: true })
        .then(r => setFornitoriList(r.data || []))
        .catch(() => setFornitoriList([]));
    }
  }, [open]);

  const handleFilesSelected = (e) => {
    const list = Array.from(e.target.files || []);
    // Filtra solo estensioni valide
    const valid = list.filter(f => /\.(xml|p7m)$/i.test(f.name));
    if (valid.length !== list.length) {
      toast.warning(`${list.length - valid.length} file ignorati (solo .xml o .p7m accettati)`);
    }
    setFiles(valid);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer.files || []);
    const valid = list.filter(f => /\.(xml|p7m)$/i.test(f.name));
    if (valid.length !== list.length) {
      toast.warning(`${list.length - valid.length} file ignorati (solo .xml o .p7m accettati)`);
    }
    setFiles(valid);
  };

  const submitPreview = async () => {
    if (files.length === 0) { toast.error('Seleziona almeno un file'); return; }
    setUploading(true);
    const t = toast.loading(`Analisi di ${files.length} file…`);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await axios.post(`${API}/fatture-entrata/import-xml/preview`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      // Default: per ogni nuovo fornitore azione = "crea"
      const defaultsN = {};
      (r.data.nuovi_fornitori || []).forEach(nf => {
        defaultsN[nf.ref] = { azione: 'crea', ...nf };
      });
      setNuoviScelte(defaultsN);
      // Default: fatture non saltate a meno che già importate (che restano OBBLIGATORIAMENTE saltate)
      const defaultsF = {};
      (r.data.fatture || []).forEach((f, i) => {
        defaultsF[i] = { salta: f.gia_importata === true };
      });
      setFattureScelte(defaultsF);
      // Espandi tutti gli anni di default (mesi collassati)
      const groups = groupFattureByYearMonth(r.data.fatture || []);
      const expanded = new Set();
      groups.forEach(g => expanded.add(`y-${g.anno}`));
      setExpandedGroups(expanded);
      setSelectedIndexes(new Set());
      setStep(2);
      toast.dismiss(t);
      const totOk = r.data.fatture?.length || 0;
      const totErr = r.data.errori?.length || 0;
      toast.success(`Trovate ${totOk} fatture · ${totErr} errori`);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || 'Errore analisi XML');
    } finally {
      setUploading(false);
    }
  };

  const confirmImport = async () => {
    if (!preview) return;
    setConfirming(true);
    const t = toast.loading('Creazione fornitori e fatture…');
    try {
      // Costruisci payload
      const nuovi_fornitori = Object.entries(nuoviScelte).map(([ref, s]) => ({
        ref,
        azione: s.azione,
        fornitore_id_esistente: s.fornitore_id_esistente || '',
        ragione_sociale: s.ragione_sociale || '',
        partita_iva: s.partita_iva || '',
        codice_fiscale: s.codice_fiscale || '',
        indirizzo: s.indirizzo || '',
        cap: s.cap || '',
        citta: s.citta || '',
        provincia: s.provincia || '',
        telefono: s.telefono || '',
        email: s.email || '',
      }));
      const fatture = (preview.fatture || []).map((f, i) => ({
        filename: f.filename,
        fornitore_ref: f.fornitore_ref,
        numero: f.numero,
        data: f.data,
        scadenza: f.scadenza || '',
        imponibile: f.imponibile || 0,
        iva: f.iva || 0,
        aliquota_iva: f.aliquota_iva || 22,
        importo: f.importo || 0,
        tipo_documento: f.tipo_documento || 'fattura',
        descrizione: f.descrizione || '',
        stato: 'da_pagare',
        // Safety net: i duplicati sono SEMPRE saltati, indipendentemente da fattureScelte
        salta: f.gia_importata === true ? true : (fattureScelte[i]?.salta === true),
      }));
      const r = await axios.post(`${API}/fatture-entrata/import-xml/conferma`,
        { nuovi_fornitori, fatture },
        { withCredentials: true });
      setRisultato(r.data);
      setStep(3);
      toast.dismiss(t);
      toast.success(`Importate ${r.data.fatture_create?.length || 0} fatture`);
      if (onImported) onImported();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || 'Errore conferma import');
    } finally {
      setConfirming(false);
    }
  };

  const updateNuovoFornitore = (ref, patch) => {
    setNuoviScelte(prev => ({ ...prev, [ref]: { ...prev[ref], ...patch } }));
  };

  // useCallback con deps vuote + previewRef: identità stabile a ogni render,
  // così FatturaRow (React.memo) non viene invalidato quando cambiano
  // fattureScelte/selectedIndexes di righe diverse dalla propria.
  const toggleFatturaSalta = useCallback((i) => {
    // I duplicati (gia_importata) NON possono essere riabilitati.
    const fatt = previewRef.current?.fatture?.[i];
    if (fatt?.gia_importata) return;
    setFattureScelte(prev => ({ ...prev, [i]: { ...prev[i], salta: !prev[i]?.salta } }));
  }, []);

  // --- Bulk selection helpers ---
  const toggleGroupExpand = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleIndexSelection = useCallback((i) => {
    if (previewRef.current?.fatture?.[i]?.gia_importata) return; // no select su duplicati
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }, []);

  // Il raggruppamento anno/mese dipende solo dai dati della preview, non dalla
  // selezione: senza useMemo veniva ricalcolato su TUTTE le fatture a ogni
  // click di una checkbox.
  const fattureGroups = useMemo(
    () => groupFattureByYearMonth(preview?.fatture || []),
    [preview]
  );

  const selezionabili = (preview?.fatture || [])
    .map((f, i) => i)
    .filter(i => !preview.fatture[i].gia_importata);

  const allSelected = selezionabili.length > 0 && selezionabili.every(i => selectedIndexes.has(i));
  const someSelected = selezionabili.some(i => selectedIndexes.has(i));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIndexes(new Set());
    } else {
      setSelectedIndexes(new Set(selezionabili));
    }
  };

  const selectGroupMonth = (items, checked) => {
    // items: [{fattura, originalIndex}]
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      items.forEach(x => {
        if (x.fattura.gia_importata) return;
        if (checked) next.add(x.originalIndex); else next.delete(x.originalIndex);
      });
      return next;
    });
  };

  // Applica un'azione bulk alle fatture attualmente selezionate.
  const bulkAction = (action) => {
    if (selectedIndexes.size === 0) { toast.error('Nessuna fattura selezionata'); return; }
    const indexes = Array.from(selectedIndexes);
    if (action === 'salta') {
      setFattureScelte(prev => {
        const next = { ...prev };
        indexes.forEach(i => { next[i] = { ...next[i], salta: true }; });
        return next;
      });
      toast.success(`${indexes.length} fatture marcate come "Salta"`);
    } else if (action === 'importa') {
      setFattureScelte(prev => {
        const next = { ...prev };
        indexes.forEach(i => {
          if (!preview.fatture[i].gia_importata) next[i] = { ...next[i], salta: false };
        });
        return next;
      });
      toast.success(`${indexes.length} fatture marcate come "Importa"`);
    }
    setSelectedIndexes(new Set()); // reset selezione dopo azione
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="import-xml-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-slate-500" />
            Importa fatture ricevute (XML SDI) — in massa
          </DialogTitle>
          <DialogDescription>
            Carica uno o più file <strong>.xml</strong> o <strong>.xml.p7m</strong> (Fattura Elettronica).
            Verifica i dati, conferma o crea nuovi fornitori, poi importa in blocco.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto pr-1">
          {/* STEP 1 — Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:bg-slate-50 hover:border-blue-400 transition-colors"
                data-testid="drop-zone-xml"
              >
                <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">Trascina qui i file XML o clicca per selezionarli</p>
                <p className="text-xs text-slate-500 mt-1">Sono accettati file <code>.xml</code> e <code>.xml.p7m</code> (formato FatturaPA v1.2)</p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".xml,.p7m"
                  className="hidden"
                  onChange={handleFilesSelected}
                  data-testid="file-input-xml"
                />
              </div>
              {files.length > 0 && (
                <div className="border rounded-lg bg-slate-50" data-testid="selected-files-list">
                  <div className="p-3 border-b flex items-center justify-between">
                    <p className="text-sm font-medium">{files.length} file selezionati</p>
                    <Button variant="ghost" size="sm" onClick={() => setFiles([])}>
                      <X className="h-4 w-4 mr-1" />Rimuovi tutti
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                    {files.map((f, i) => (
                      <div key={i} className="text-xs bg-white rounded px-2 py-1 flex justify-between border">
                        <span className="truncate">{f.name}</span>
                        <span className="text-slate-400 ml-2">{(f.size / 1024).toFixed(1)} KB</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  onClick={submitPreview}
                  disabled={uploading || files.length === 0}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="analizza-btn"
                >
                  {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analisi…</> : <><ArrowRight className="h-4 w-4 mr-2" />Analizza {files.length} file</>}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2 — Preview & conferma */}
          {step === 2 && preview && (
            <div className="space-y-4">
              {/* Sommario */}
              <div className="grid grid-cols-4 gap-2">
                <div className="rounded border p-2 bg-blue-50 border-blue-200">
                  <p className="text-[10px] uppercase text-blue-700 font-semibold">Fatture trovate</p>
                  <p className="text-lg font-bold text-blue-900">{preview.fatture?.length || 0}</p>
                </div>
                <div className="rounded border p-2 bg-emerald-50 border-emerald-200">
                  <p className="text-[10px] uppercase text-emerald-700 font-semibold">Fornitori match</p>
                  <p className="text-lg font-bold text-emerald-900">{preview.fornitori_matchati?.length || 0}</p>
                </div>
                <div className="rounded border p-2 bg-amber-50 border-amber-200">
                  <p className="text-[10px] uppercase text-amber-700 font-semibold">Nuovi fornitori</p>
                  <p className="text-lg font-bold text-amber-900">{preview.nuovi_fornitori?.length || 0}</p>
                </div>
                <div className={`rounded border p-2 ${(preview.errori?.length || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-[10px] uppercase font-semibold ${(preview.errori?.length || 0) > 0 ? 'text-red-700' : 'text-slate-600'}`}>File errati</p>
                  <p className={`text-lg font-bold ${(preview.errori?.length || 0) > 0 ? 'text-red-900' : 'text-slate-900'}`}>{preview.errori?.length || 0}</p>
                </div>
              </div>

              {/* Errori */}
              {preview.errori?.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />File con errori (verranno ignorati)
                  </p>
                  <ul className="text-xs space-y-1">
                    {preview.errori.map((e, i) => (
                      <li key={i} data-testid={`error-row-${i}`}>
                        <strong>{e.filename}:</strong> {e.motivo}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Nuovi fornitori — richiedono conferma */}
              {preview.nuovi_fornitori?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-amber-50 border-b border-amber-200 px-3 py-2">
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                      <Building2 className="h-4 w-4" />Nuovi fornitori — Conferma prima di creare
                    </p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Per ogni fornitore trovato negli XML che non esiste in anagrafica, scegli se crearlo, collegarlo a uno esistente o saltarlo.
                    </p>
                  </div>
                  <div className="p-2 space-y-2 max-h-60 overflow-y-auto">
                    {preview.nuovi_fornitori.map((nf) => {
                      const s = nuoviScelte[nf.ref] || { azione: 'crea' };
                      return (
                        <div key={nf.ref} className="border rounded p-2 bg-white" data-testid={`nuovo-fornitore-${nf.ref}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-900 truncate">{nf.ragione_sociale}</p>
                              <p className="text-xs text-slate-500 font-mono">
                                {nf.partita_iva ? `P.IVA ${nf.partita_iva}` : 'senza P.IVA'}
                                {nf.citta && ` · ${nf.citta}${nf.provincia ? ` (${nf.provincia})` : ''}`}
                              </p>
                            </div>
                            <Select value={s.azione} onValueChange={(v) => updateNuovoFornitore(nf.ref, { azione: v })}>
                              <SelectTrigger className="w-40 h-8" data-testid={`azione-select-${nf.ref}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="crea">✅ Crea nuovo</SelectItem>
                                <SelectItem value="collega">🔗 Collega a esistente</SelectItem>
                                <SelectItem value="salta">⏭️ Salta fatture</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {s.azione === 'collega' && (
                            <div className="mt-2">
                              <Label className="text-xs">Fornitore esistente</Label>
                              <Select
                                value={s.fornitore_id_esistente || ''}
                                onValueChange={(v) => updateNuovoFornitore(nf.ref, { fornitore_id_esistente: v })}
                              >
                                <SelectTrigger className="h-8" data-testid={`collega-select-${nf.ref}`}>
                                  <SelectValue placeholder="Scegli un fornitore…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {fornitoriList.map(f => (
                                    <SelectItem key={f.id} value={f.id}>
                                      {f.ragione_sociale || f.name} {f.partita_iva ? `· ${f.partita_iva}` : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {s.azione === 'crea' && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div>
                                <Label className="text-[10px]">Ragione sociale</Label>
                                <Input
                                  className="h-7 text-xs"
                                  value={s.ragione_sociale || ''}
                                  onChange={(e) => updateNuovoFornitore(nf.ref, { ragione_sociale: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px]">P.IVA</Label>
                                <Input
                                  className="h-7 text-xs font-mono"
                                  value={s.partita_iva || ''}
                                  onChange={(e) => updateNuovoFornitore(nf.ref, { partita_iva: e.target.value })}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Elenco fatture raggruppato per anno/mese con bulk actions */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <FileCheck2 className="h-4 w-4 text-slate-500" />
                    <p className="text-sm font-semibold text-slate-700">
                      Fatture da importare ({preview.fatture?.length || 0})
                    </p>
                  </div>
                  {/* Bulk toolbar */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-white border hover:bg-slate-100"
                      data-testid="bulk-select-all"
                      title={allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
                    >
                      {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                      {selectedIndexes.size > 0 ? `${selectedIndexes.size} selezionate` : 'Seleziona tutti'}
                    </button>
                    {someSelected && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          onClick={() => bulkAction('importa')}
                          data-testid="bulk-importa-btn"
                        >
                          ✅ Importa selezionate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200"
                          onClick={() => bulkAction('salta')}
                          data-testid="bulk-salta-btn"
                        >
                          ⏭️ Salta selezionate
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {fattureGroups.map((group) => {
                    const yKey = `y-${group.anno}`;
                    const yOpen = expandedGroups.has(yKey);
                    return (
                      <div key={yKey} className="border-b last:border-b-0">
                        {/* Header anno */}
                        <button
                          type="button"
                          onClick={() => toggleGroupExpand(yKey)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-left"
                          data-testid={`group-year-${group.anno}`}
                        >
                          <div className="flex items-center gap-2">
                            {yOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            <span className="font-semibold text-sm">{group.anno || 'Senza anno'}</span>
                            <span className="text-xs text-slate-500">
                              ({group.count} {group.count === 1 ? 'fattura' : 'fatture'})
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-slate-700">€ {fmt(group.totale)}</span>
                        </button>

                        {yOpen && group.mesi.map((mese) => {
                          const mKey = `y-${group.anno}-m-${mese.mese}`;
                          const mOpen = expandedGroups.has(mKey) || group.mesi.length === 1; // se un solo mese, sempre aperto
                          const isMeseAllSelected = mese.items.every(x =>
                            x.fattura.gia_importata || selectedIndexes.has(x.originalIndex)
                          );
                          return (
                            <div key={mKey} className="border-t">
                              {/* Header mese */}
                              <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50">
                                <button
                                  type="button"
                                  onClick={() => toggleGroupExpand(mKey)}
                                  className="flex items-center gap-2 flex-1 text-left hover:text-blue-700"
                                  data-testid={`group-month-${group.anno}-${mese.mese}`}
                                >
                                  {mOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                  <span className="text-xs font-medium">{mese.label}</span>
                                  <span className="text-[10px] text-slate-500">
                                    ({mese.count} · € {fmt(mese.totale)})
                                  </span>
                                </button>
                                <Checkbox
                                  checked={isMeseAllSelected}
                                  onCheckedChange={(v) => selectGroupMonth(mese.items, v)}
                                  aria-label={`Seleziona ${mese.label}`}
                                  data-testid={`select-month-${group.anno}-${mese.mese}`}
                                />
                              </div>

                              {mOpen && (
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-white">
                                      <TableHead className="w-8"></TableHead>
                                      <TableHead className="w-8 text-xs">Imp.</TableHead>
                                      <TableHead className="text-xs">Fornitore</TableHead>
                                      <TableHead className="text-xs">Numero</TableHead>
                                      <TableHead className="text-xs">Data</TableHead>
                                      <TableHead className="text-xs">Scad.</TableHead>
                                      <TableHead className="text-xs text-right">Imp.</TableHead>
                                      <TableHead className="text-xs text-right">IVA</TableHead>
                                      <TableHead className="text-xs text-right">Totale</TableHead>
                                      <TableHead className="text-xs">Stato</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {mese.items.map(({ fattura: f, originalIndex: i }) => (
                                      <FatturaRow
                                        key={i}
                                        index={i}
                                        fattura={f}
                                        salta={fattureScelte[i]?.salta === true}
                                        duplicato={f.gia_importata === true}
                                        isSelected={selectedIndexes.has(i)}
                                        onToggleSelect={toggleIndexSelection}
                                        onToggleSalta={toggleFatturaSalta}
                                      />
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={() => setStep(1)} disabled={confirming}>
                  <RotateCcw className="h-4 w-4 mr-1" />Cambia file
                </Button>
                <Button
                  onClick={confirmImport}
                  disabled={confirming || (preview.fatture?.length || 0) === 0}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="conferma-import-btn"
                >
                  {confirming ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importazione…</> : <>✅ Conferma e importa</>}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3 — Riepilogo */}
          {step === 3 && risultato && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded border p-3 bg-emerald-50 border-emerald-200">
                  <p className="text-[10px] uppercase text-emerald-700 font-semibold">Fatture importate</p>
                  <p className="text-2xl font-bold text-emerald-900">{risultato.fatture_create?.length || 0}</p>
                </div>
                <div className="rounded border p-3 bg-amber-50 border-amber-200">
                  <p className="text-[10px] uppercase text-amber-700 font-semibold">Fatture saltate</p>
                  <p className="text-2xl font-bold text-amber-900">{risultato.fatture_saltate?.length || 0}</p>
                </div>
                <div className="rounded border p-3 bg-blue-50 border-blue-200">
                  <p className="text-[10px] uppercase text-blue-700 font-semibold">Fornitori creati</p>
                  <p className="text-2xl font-bold text-blue-900">{risultato.fornitori_creati?.length || 0}</p>
                </div>
              </div>

              {risultato.fatture_create?.length > 0 && (
                <div className="border rounded">
                  <p className="text-sm font-semibold px-3 py-2 border-b bg-emerald-50">Fatture create</p>
                  <div className="max-h-40 overflow-y-auto text-xs">
                    {risultato.fatture_create.map((f, i) => (
                      <div key={i} className="px-3 py-1 border-b last:border-b-0 flex justify-between">
                        <span><span className="font-mono">{f.protocollo}</span> · {f.fornitore} · {f.numero}</span>
                        <span className="font-semibold">€ {fmt(f.importo)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {risultato.fatture_saltate?.length > 0 && (
                <div className="border rounded">
                  <p className="text-sm font-semibold px-3 py-2 border-b bg-amber-50">Fatture saltate</p>
                  <div className="max-h-32 overflow-y-auto text-xs">
                    {risultato.fatture_saltate.map((f, i) => (
                      <div key={i} className="px-3 py-1 border-b last:border-b-0">
                        {f.numero} <span className="text-slate-500">— {f.motivo || 'saltata dall\'utente'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={onClose} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="chiudi-import-btn">
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
